'use client';

import { CART_DURATION_HALF_DAY, type CartDuration } from '@funkey/constants';
import { DateTimeSDK, maybeMap } from '@funkey/shared';
import { graphql } from '@funkey/types/graphql/gql';
import { CACHED_PARTICIPANTS_FILTER } from '~/features/activities/search/client/context';
import { CheckoutInfoFieldset } from '~/features/cart/checkout/info/CheckoutInfoFieldset';
import { api } from '~/modules';
import { processGraphqlFormErrors } from '~/utils/errors';
import { useFormState, useValidateWithFeedback } from '~/modules/form';
import { useI18n } from '~/features/i18n';
import useNotifications from '~/features/notifications/useNotifications';
import { usePortalHREF_relative, usePortalRouter } from '~/features/global/portal';
import { useSuspenseQuery } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { useTRPC } from '~/modules/trpc/client';
import { notFound } from 'next/navigation';
import { useCartCtx } from '~/features/cart/context';
import { CheckoutPageTemplate } from '~/features/cart/checkout/CheckoutPageTemplate';
import { PORTAL_RELATIVE_HREF_CART, PORTAL_RELATIVE_HREF_CHECKOUT } from '~/features/cart/hooks';

export const CheckoutInfoPage: React.FC = () => {
    const i18n = useI18n();
    const router = usePortalRouter();
    const { dispatchGraphqlError } = useNotifications();
    const trpc = useTRPC();
    const cartCtx = useCartCtx();

    // AB testing logic - single source of truth
    const cartQuery = useSuspenseQuery(
        trpc.cart.get.queryOptions(undefined, {
            select: data => (data ? data : notFound()),
        }),
    );

    const initialValues = useMemo<CheckoutInfoFieldset.FormValues>(
        () => ({
            ...cartQuery.data,
            userProvidesLocation: cartQuery.data.userProvidesLocation ?? false,
            address: cartQuery.data.address || '',
            durationType: (cartQuery.data.durationType || CART_DURATION_HALF_DAY) as CartDuration,
            participants:
                CACHED_PARTICIPANTS_FILTER.getValue() ??
                cartQuery.data.participants ??
                CACHED_PARTICIPANTS_FILTER.initialValue,
            startTime: maybeMap(cartQuery.data.startsAt, DateTimeSDK.toLocaleTimeStr),
            radius: cartQuery.data.radius || 10,
            budget: cartQuery.data.budget || undefined,
            budgetType: cartQuery.data.budgetType || null,
            startsAt: cartQuery.data.startsAt?.toString() ?? null,
            endsAt: cartQuery.data.endsAt?.toString() ?? null,
            dateTimeIndication: cartQuery.data.meta.dateTimeIndication || null,
        }),
        [cartQuery.data],
    );

    const formState = useFormState<CheckoutInfoFieldset.FormValues>(
        initialValues,
        CheckoutInfoFieldset.useValidation(i18n),
    );
    const validateWithFeedback = useValidateWithFeedback(formState);

    useEffect(() => {
        if (
            typeof formState.values.participants === 'number' &&
            formState.values.participants >= 1 &&
            CACHED_PARTICIPANTS_FILTER.getValue() !== formState.values.participants
        ) {
            CACHED_PARTICIPANTS_FILTER.setValue(formState.values.participants);
        }
    }, [formState.values.participants]);

    const updateCheckoutInfo = api.useGraphqlMutation(UPDATE_CHECKOUT_INFO_MUTATION, {
        throwOnError: false,
        onSuccess() {
            cartCtx.invalidateQueries();
            router.push(PORTAL_RELATIVE_HREF_CHECKOUT('extra'));
        },
        onError(e) {
            dispatchGraphqlError(e);
            formState.updateErrors(processGraphqlFormErrors(e));
        },
    });

    return (
        <CheckoutPageTemplate
            previousButton={{
                href: usePortalHREF_relative(PORTAL_RELATIVE_HREF_CART),
                label: i18n._('checkout.cta.back-to-cart', undefined, {
                    message: 'Back to cart',
                }),
            }}
            nextButton={{
                label: i18n._('checkout.cta.to-extras', undefined, {
                    message: 'Choose extras',
                }),
                async onClick() {
                    if (validateWithFeedback() === false) return;

                    await updateCheckoutInfo.mutateAsync({
                        cartId: cartQuery.data.id.toString(),
                        input: {
                            address: formState.values.address,
                            budget: formState.values.budget,
                            endsAt: formState.values.endsAt
                                ? DateTimeSDK.formatNativeDate(new Date(formState.values.endsAt))
                                : null,
                            // Check if we want to store the time, if no date was selected (currently not possible due to combined field)
                            startsAt:
                                CheckoutInfoFieldset.parseStartsAt(
                                    formState.values.startsAt,
                                    formState.values.startTime,
                                )?.toISOString() ?? null,
                            radius: formState.values.radius,
                            budgetType: formState.values.budgetType,
                            durationType: formState.values.durationType,
                            participants: formState.values.participants ?? 0,
                            dateTimeIndication: formState.values.dateTimeIndication,
                            userProvidesLocation: formState.values.userProvidesLocation ?? false,
                        },
                    });
                },
            }}
        >
            <CheckoutInfoFieldset.Component formState={formState} i18n={i18n} />
        </CheckoutPageTemplate>
    );
};

const UPDATE_CHECKOUT_INFO_MUTATION = graphql(`
    mutation updateCheckoutInfo($cartId: ID!, $input: UpdateCheckoutInfoInput!) {
        updateCheckoutInfo(cartId: $cartId, input: $input) {
            cart {
                ...DefaultCart
            }
        }
    }
`);
